/**
 * Agent Base Class
 *
 * Unified base class for all agents (top-level and sub-agents).
 * Supports both simple orchestration and agent-as-tool patterns.
 *
 * @template TParams - The expected parameter type for the agent
 * @template TResponse - The response type returned by the agent
 */
import { ToolBase, hasStreamingSupport } from "./toolDescription";
import { extractTextContent } from "../../llm/baseAdapter";
import { getAgentServices } from "../../services";
let authService;
try {
    authService = require("@carousel-office/shared").authService;
}
catch { /* shared not available in headless */ }
import { safeParseToolParams } from "./toolDescription";
import { generateId } from "../../idGenerator";
import { detectImageMimeType } from "../../utils/imageUtils";
import { SubmitResultsTool, isSubmitResultsResponse } from "./submitResults.tool";
import { pushAgentContext, popAgentContext } from "../../utils/agentContext";
function extractImageIds(result) {
    if (!result || typeof result !== 'object')
        return [];
    const obj = result;
    const ids = [];
    if (typeof obj._imageId === 'string')
        ids.push(obj._imageId);
    if (Array.isArray(obj._imageIds))
        ids.push(...obj._imageIds.filter((id) => typeof id === 'string'));
    return ids;
}
let _consumeStreamingToolCalls;
try {
    const streaming = require("@carousel-office/llm-streaming");
    _consumeStreamingToolCalls = streaming.consumeStreamingToolCalls;
}
catch { /* llm-streaming not available in headless mode */ }
const consumeStreamingToolCalls = _consumeStreamingToolCalls;
// ═══════════════════════════════════════════════════════════════════════════
// AGENT TYPE ENUM
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Types of agents in the system
 */
export var AgentType;
(function (AgentType) {
    // Top-level agents
    AgentType["ORCHESTRATOR"] = "livePowerPointOrchestrator";
    // Research agents
    AgentType["RESEARCH"] = "research";
    AgentType["ALPHASENSE"] = "alphasense";
    AgentType["IMAGE_SEARCH"] = "image_search";
    AgentType["EXTERNAL_FILE"] = "external_file";
    AgentType["IN_PRESENTATION"] = "in_presentation";
    // Execution agents
    AgentType["WRITE_SLIDE"] = "write_slide";
    AgentType["LIVE_EDITOR"] = "live_editor";
    AgentType["ADD_SLIDES"] = "add_slides";
    AgentType["LINKED_EXCEL"] = "linked_excel";
})(AgentType || (AgentType = {}));
// ═══════════════════════════════════════════════════════════════════════════
// AGENT BASE CLASS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Base class for all agents.
 * Extends ToolBase to support agent-as-tool patterns.
 *
 * @template TParams - The expected parameter type for the agent
 * @template TResponse - The response type returned by the agent
 */
export class AgentBaseClass extends ToolBase {
    /**
     * Set the streaming mode for this agent.
     * Useful when an agent is used as a tool and the parent wants to disable streaming.
     */
    setStreamMode(enabled) {
        this.stream = enabled;
    }
    constructor() {
        super();
        /**
         * Whether to use streaming mode for LLM calls.
         * - true: Stream chunks, incremental tool call parsing
         * - false: Single request, parse complete response
         */
        this.stream = true;
        /** Maximum iterations for tool loop */
        this.maxToolLoopIterations = 100;
        /**
         * Maximum tokens for LLM response.
         * Default 0 means "use model maximum" — the Keywords adapter resolves
         * this to the model's actual max (128K for Opus 4.6, 64K for Sonnet/Haiku/Gemini).
         *
         * Override with a specific value only if you need to LIMIT output below the
         * model's max (e.g., for cost control on sub-agents that don't need much output).
         */
        this.maxResponseTokens = 0;
        // ═══════════════════════════════════════════════════════════════════════
        // OPTIONAL INFRASTRUCTURE (for agent-as-tool pattern)
        // ═══════════════════════════════════════════════════════════════════════
        /** Whether to use thread management (enables thread persistence) */
        this.useThreadManagement = true;
        /** Whether to use task tracking (enables progress reporting, cancellation) */
        this.useTaskTracking = true;
        /** Whether to use context resolution (enables short ID → content) */
        this.useContextResolution = true;
        /**
         * Whether to use the Submit Results Pattern.
         * When true, the agent will have a submitResults tool auto-injected and the
         * tool loop will exit when submitResults is called, using its results directly.
         *
         * This is the RECOMMENDED pattern for all agents-as-tools because:
         * - It allows the LLM to decide when it has enough information
         * - It enables multiple search/tool iterations before committing
         * - It provides structured results to the parent agent
         */
        this.useSubmitResultsPattern = true;
        /**
         * Whether to inject retrieved images into user messages for LLM vision analysis.
         * When true, images from tool results are queued and injected for visual confirmation.
         * When false, images are still stored but NOT shown to the LLM (relies on metadata only).
         *
         * Disable this if the LLM's vision capabilities are insufficient for the task
         * (e.g., color perception issues with logos on transparent backgrounds).
         *
         * @default true
         */
        this.injectImagesForVision = true;
        /**
         * Whether this agent modifies the PowerPoint presentation.
         *
         * When true (default), an undo checkpoint is created at the start of execution
         * so all operations performed by this agent can be undone as a single unit
         * with Ctrl+Z in PowerPoint.
         *
         * Set to false for agents that only read/search without modifying
         * (e.g., PresentationLibrarySearchAgent, ImageSearchAgent, AlphaSenseAgent).
         */
        this.createsUndoCheckpoint = true;
        this.systemPrompt = "";
        this.toolCalls = [];
        // Mark as agent for tool loop identification
        this.isAgent = true;
    }
    /**
     * Best-effort mutability classification for checkpoint capture.
     * Non-mutating tools can still be jumped to by rewinding to the latest mutating checkpoint.
     */
    shouldCaptureCheckpointForTool(toolName) {
        const checker = getAgentServices().isMutatingToolName;
        return checker ? checker(toolName) : false;
    }
    /**
     * Set the parent registry for this agent.
     * Called by AgentRegistry.registerAgent() to link the agent to its registry.
     * Also propagates to any sub-agents in toolCalls that are instances of AgentBaseClass.
     */
    setParentRegistry(registry) {
        this._parentRegistry = registry;
        // Propagate to sub-agents in toolCalls
        for (const tool of this.toolCalls) {
            if (tool instanceof AgentBaseClass) {
                tool.setParentRegistry(registry);
                console.log(`[${this.name}] Propagated registry to sub-agent: ${tool.name}`);
            }
        }
    }
    /**
     * Get the parent registry, if set.
     */
    getParentRegistry() {
        return this._parentRegistry;
    }
    /**
     * Initialize agent after constructor.
     * Call this in subclass constructors AFTER setting up tools.
     * Injects submitResults tool if useSubmitResultsPattern is enabled.
     */
    initializeAgent() {
        if (this.useSubmitResultsPattern) {
            // Check if submitResults is already in the tool list
            const hasSubmitResults = this.toolCalls.some(t => t.name === 'submitResults');
            if (!hasSubmitResults) {
                this.toolCalls.push(new SubmitResultsTool());
                console.log(`[${this.name}] Injected submitResults tool`);
            }
        }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // INFRASTRUCTURE GETTERS (lazy initialization, only when enabled)
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Get agent registry (only available when useTaskTracking is true)
     * Requires agent to be registered in a conversation's AgentRegistry via setParentRegistry()
     */
    get registry() {
        if (!this.useTaskTracking) {
            throw new Error('Task tracking not enabled. Set useTaskTracking = true.');
        }
        if (!this._parentRegistry) {
            throw new Error(`[${this.name}] No parent registry set. Agent must be registered via AgentRegistry.registerAgent().`);
        }
        return this._parentRegistry;
    }
    /**
     * Get thread manager (only available when useThreadManagement is true)
     * Requires agent to be registered in a conversation's AgentRegistry via setParentRegistry()
     */
    get threadManager() {
        if (!this.useThreadManagement) {
            throw new Error('Thread management not enabled. Set useThreadManagement = true.');
        }
        if (!this._parentRegistry) {
            throw new Error(`[${this.name}] No parent registry set. Agent must be registered via AgentRegistry.registerAgent().`);
        }
        return this._parentRegistry.threadManager;
    }
    /** Get context resolver (only available when useContextResolution is true) */
    get contextResolver() {
        if (!this.useContextResolution) {
            throw new Error('Context resolution not enabled. Set useContextResolution = true.');
        }
        return getAgentServices().contextResolver;
    }
    /** Get service locator (replaces legacy GlobalStateManager access) */
    get globalState() {
        return getAgentServices();
    }
    // ═══════════════════════════════════════════════════════════════════════
    // KEYWORDS TRACKING & LOGGING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Get Keywords AI tracing metadata for LLM calls.
     * Uses conversationId as thread_identifier to group all calls in the same conversation.
     */
    getKeywordsExtraBody(iteration, toolName) {
        const user = authService?.getCurrentUser?.();
        const threadIdentifier = this.currentConversationId || 'default';
        const customerIdentifier = user?.email || 'anonymous';
        const groupIdentifier = user?.companyUuid;
        const metadata = {
            agent_name: this.name,
            agent_type: this.agentType || 'unknown',
            iteration,
            user_id: user?._id,
            user_email: user?.email,
            company_name: user?.companyName,
            tool_count: this.toolCalls.length,
            timestamp: new Date().toISOString(),
        };
        if (toolName) {
            metadata.current_tool = toolName;
            metadata.tool_name = toolName;
        }
        if (this.currentTaskId)
            metadata.task_id = this.currentTaskId;
        if (this.currentThreadId)
            metadata.thread_id = this.currentThreadId;
        if (this.currentConversationId)
            metadata.conversation_id = this.currentConversationId;
        if (this.currentdocumentId)
            metadata.deck_id = this.currentdocumentId;
        return {
            customer_identifier: customerIdentifier,
            group_identifier: groupIdentifier,
            thread_identifier: threadIdentifier,
            metadata,
        };
    }
    /**
     * Log LLM request summary (concise format for timing analysis)
     */
    logLLMRequest(messages, _systemPrompt, _iteration) {
        // Count message types and images
        const roleCounts = messages.reduce((acc, m) => {
            acc[m.role] = (acc[m.role] || 0) + 1;
            return acc;
        }, {});
        let imageCount = 0;
        for (const msg of messages) {
            if (Array.isArray(msg.content)) {
                imageCount += msg.content.filter(p => p.type === 'image_url').length;
            }
        }
        const summary = Object.entries(roleCounts).map(([k, v]) => `${k}:${v}`).join(', ');
        const imageInfo = imageCount > 0 ? `, ${imageCount} images` : '';
        console.log(`[${this.name}] 📤 LLM REQUEST: ${messages.length} msgs (${summary}${imageInfo}), ${this.toolCalls.length} tools`);
    }
    /**
     * Execute the agent.
     *
     * This is the main entry point - a single shared implementation that:
     * 1. Sets up infrastructure (task tracking, context resolution) if enabled
     * 2. Generates system prompt via generateSystemPrompt()
     * 3. Builds initial messages via buildInitialMessages()
     * 4. Runs the tool loop (sends to LLM, handles tool calls)
     * 5. Processes results via processResults()
     * 6. Returns structured result
     *
     * Subclasses customize behavior by overriding hooks, not the execute() method.
     *
     * @param parameters - Input parameters (type unknown for runtime validation)
     * @param callbacks - Optional callbacks for streaming/progress
     * @returns Agent response
     */
    async execute(parameters, callbacks) {
        // ═══════════════════════════════════════════════════════════════════════
        // 1. VALIDATE & SETUP
        // ═══════════════════════════════════════════════════════════════════════
        // Pre-process and validate parameters
        const processedParams = this.preprocessParameters(parameters);
        let params;
        if (this.paramsSchema) {
            const parseResult = safeParseToolParams(this.paramsSchema, processedParams);
            if (parseResult.success === false) {
                return this.buildErrorResult(`Invalid parameters: ${parseResult.error}`);
            }
            params = parseResult.data;
        }
        else {
            params = processedParams;
        }
        // Store params for use in buildFinalResult
        this.lastParams = params;
        // Get conversation/deck context (required for proper thread management)
        const conversationId = parameters?.conversationId;
        const documentId = parameters?.documentId;
        // Store for use in runToolLoop when creating sub-agent threads and for tracing
        this.currentConversationId = conversationId || 'default';
        this.currentdocumentId = documentId || 'default';
        if (!conversationId || !documentId) {
            console.warn(`[${this.name}] Missing conversationId or documentId - thread isolation may not work correctly`);
        }
        // ═══════════════════════════════════════════════════════════════════════
        // 1.5. CREATE UNDO CHECKPOINT (if agent modifies presentation)
        // ═══════════════════════════════════════════════════════════════════════
        // Creates a new entry in PowerPoint's undo stack so all operations
        // performed by this agent can be undone as a single unit with Ctrl+Z.
        if (this.createsUndoCheckpoint && documentId && documentId !== 'default') {
            try {
                const bc = getAgentServices().backendClient;
                if (bc?.createUndoCheckpoint)
                    await bc.createUndoCheckpoint(documentId, this.name);
            }
            catch (undoError) {
                // Non-fatal - log and continue
                console.warn(`[${this.name}] Could not create undo checkpoint:`, undoError);
            }
        }
        // ═══════════════════════════════════════════════════════════════════════
        // 2. INFRASTRUCTURE SETUP (optional - for agent-as-tool pattern)
        // ═══════════════════════════════════════════════════════════════════════
        let taskId;
        let threadId;
        let cancellationToken;
        let resolvedContext = [];
        const agentCallbacks = callbacks;
        if (this.useTaskTracking && this.agentType) {
            const task = this.registry.createTask({
                agentType: this.agentType,
                agentName: this.name,
                conversationId,
                documentId,
                threadId: params.threadId,
            });
            taskId = task.taskId;
            threadId = task.threadId;
            cancellationToken = task.cancellationToken;
            if (agentCallbacks) {
                this.registry.registerCallbacks(taskId, agentCallbacks);
            }
            this.registry.updateStatus(taskId);
        }
        else {
            // Create a default thread ID for non-tracked execution
            threadId = `${this.name}_thread_${Date.now()}`;
        }
        // ═══════════════════════════════════════════════════════════════════
        // 2.6. COMBINE CANCELLATION TOKENS (parent + own)
        // ═══════════════════════════════════════════════════════════════════
        // If parent passed a cancellation token, we need to check BOTH our own
        // token AND the parent's token for cooperative cancellation across the
        // agent hierarchy.
        const parentToken = agentCallbacks?.parentCancellationToken;
        const ownToken = cancellationToken;
        // Create combined token that checks both parent and own
        const combinedCancellationToken = {
            get isCancelled() {
                return (parentToken?.isCancelled ?? false) || (ownToken?.isCancelled ?? false);
            },
            cancel() {
                ownToken?.cancel();
            },
            throwIfCancelled() {
                if (parentToken?.isCancelled) {
                    throw new Error('Parent task was cancelled');
                }
                ownToken?.throwIfCancelled();
            },
        };
        // Store for use in runToolLoop when calling sub-agents and for tracing
        this.currentTaskId = taskId;
        this.currentThreadId = threadId;
        this.currentCancellationToken = combinedCancellationToken;
        // Push agent context for tools to access (for tracing)
        // Note: We use conversationId (not threadId) for thread_identifier because threadId includes agentType
        pushAgentContext({
            conversationId: this.currentConversationId || 'default',
            agentName: this.name,
            taskId: this.currentTaskId,
            threadId: this.currentThreadId, // threadId is for thread management, not tracing
            documentId: this.currentdocumentId || 'default',
        });
        // Use combined token going forward
        cancellationToken = combinedCancellationToken;
        // ═══════════════════════════════════════════════════════════════════
        // 2.7. SET ACTIVE THREAD
        // ═══════════════════════════════════════════════════════════════════
        // Active thread is THE thread currently being worked on.
        // For orchestrator: this is the main conversation thread
        // For sub-agents: parent sets this before calling, we confirm it
        if (this.useThreadManagement && threadId) {
            this.threadManager.setActiveThread(threadId);
        }
        if (this.useContextResolution && params.context) {
            // ARCHITECTURE: Pass documentId to resolver for conversation-scoped resolution
            // This ensures simple IDs like "slide_318" resolve to THIS conversation's deck,
            // not whatever deck is currently active in the UI
            const getCtxItems = getAgentServices().getContextItems;
            resolvedContext = getCtxItems ? getCtxItems(params.context, documentId // Conversation's anchored deck ID
            ) : [];
        }
        // Build execution context for hooks
        const context = {
            params,
            threadId: threadId,
            taskId: taskId || `${this.name}_${Date.now()}`,
            cancellationToken: cancellationToken || { throwIfCancelled: () => { } },
            resolvedContext,
            conversationId,
            documentId,
            callbacks: agentCallbacks,
        };
        // ═══════════════════════════════════════════════════════════════════
        // Execute directly - tracing is handled via thread_identifier in getKeywordsExtraBody()
        // All calls in the conversation use the same thread_identifier (conversationId)
        // which groups them into a single trace in Keywords AI
        return await this.executeWithTracing(context);
    }
    /**
     * Internal execution method (called with tracing wrapper)
     */
    async executeWithTracing(context) {
        const { params, threadId, taskId, cancellationToken, resolvedContext, conversationId, documentId, callbacks: agentCallbacks } = context;
        const executeStartTime = performance.now();
        try {
            // ═══════════════════════════════════════════════════════════════════
            // 3. BEFORE EXECUTE HOOK (agent-specific preparation)
            // ═══════════════════════════════════════════════════════════════════
            await this.beforeExecute(context);
            // ═══════════════════════════════════════════════════════════════════
            // 4. GENERATE PROMPT & BUILD NEW USER MESSAGE
            // ═══════════════════════════════════════════════════════════════════
            const systemPrompt = this.generateSystemPrompt(resolvedContext);
            const newUserMessages = this.buildInitialMessages(params, resolvedContext);
            // ═══════════════════════════════════════════════════════════════════
            // 4.5. INJECT REMINDERS INTO USER MESSAGES
            // ═══════════════════════════════════════════════════════════════════
            // Reminders provide invisible context (presentation state, security warnings)
            // wrapped in <non_user_automated_reminder> tags.
            // We store FULL messages (with reminders) in thread for tracking.
            const messagesWithReminders = await this.injectReminders(newUserMessages);
            // ═══════════════════════════════════════════════════════════════════
            // 4.6. ADD USER MESSAGE TO THREAD & GET FULL HISTORY
            // ═══════════════════════════════════════════════════════════════════
            let fullHistory;
            if (this.useThreadManagement && threadId) {
                // Add new user message(s) WITH REMINDERS to thread (store full data)
                this.threadManager.addMessages(threadId, messagesWithReminders);
                // Get full conversation history from thread (includes new message)
                fullHistory = this.threadManager.getMessages(threadId);
                // Concise history summary
                const roleCounts = fullHistory.reduce((acc, msg) => {
                    acc[msg.role] = (acc[msg.role] || 0) + 1;
                    return acc;
                }, {});
                console.log(`[${this.name}] 📜 Thread ${threadId}: ${fullHistory.length} messages (${Object.entries(roleCounts).map(([k, v]) => `${k}:${v}`).join(', ')})`);
            }
            else {
                // No thread management - use only new messages
                fullHistory = messagesWithReminders;
                console.log(`[${this.name}] ⚠️ Thread management disabled - using only new messages`);
            }
            // ═══════════════════════════════════════════════════════════════════
            // 4.7. PREPARE MESSAGES FOR LLM (strip old reminders)
            // ═══════════════════════════════════════════════════════════════════
            // Strip reminders from old messages (save tokens), keep fresh on last
            const preparedMessages = this.prepareMessagesForLlm(fullHistory);
            // Report initial progress
            if (taskId) {
                this.registry.reportProgress(taskId, 10, 'Starting...');
            }
            // ═══════════════════════════════════════════════════════════════════
            // 5. RUN TOOL LOOP (with prepared messages)
            // ═══════════════════════════════════════════════════════════════════
            const toolLoopOptions = this.buildToolLoopOptions(systemPrompt, agentCallbacks);
            const loopResult = await this.runToolLoop(preparedMessages, toolLoopOptions);
            // Store loop result for use in buildFinalResult
            this.lastLoopResult = loopResult;
            // Check for errors
            if (!loopResult.completed && loopResult.error) {
                console.error(`[${this.name}] Tool loop error:`, loopResult.error);
                if (taskId)
                    this.registry.failTask(taskId, loopResult.error);
                return this.buildErrorResult(loopResult.error, threadId);
            }
            // ═══════════════════════════════════════════════════════════════════
            // 6. UPDATE THREAD WITH ALL NEW MESSAGES (if enabled)
            // ═══════════════════════════════════════════════════════════════════
            if (this.useThreadManagement && threadId) {
                if (loopResult.tokensUsed) {
                    this.threadManager.updateTokenUsage(threadId, loopResult.tokensUsed);
                }
                // Add ALL new messages from the loop (tool calls, tool results, final response)
                // The loop started with `preparedMessages.length` items, so new ones are after that index
                const newMessages = loopResult.messages.slice(preparedMessages.length);
                if (newMessages.length > 0) {
                    this.threadManager.addMessages(threadId, newMessages);
                    console.log(`[${this.name}] Added ${newMessages.length} messages to thread ${threadId}`);
                }
            }
            // Report progress
            if (taskId) {
                this.registry.reportProgress(taskId, 80, 'Processing results...');
            }
            // ═══════════════════════════════════════════════════════════════════
            // 7. PROCESS RESULTS (agent-specific result extraction)
            // ═══════════════════════════════════════════════════════════════════
            let results;
            let summary;
            // If Submit Results Pattern was used and we have submitted results, use those
            if (this.useSubmitResultsPattern && loopResult.submittedResults) {
                console.log(`[${this.name}] Using submitted results (${loopResult.submittedResults.results.length} items)`);
                results = await this.processSubmittedResults(loopResult.submittedResults, params, context);
                // Extract summary from submitted results
                summary = loopResult.submittedResults.summary;
            }
            else {
                // Fall back to traditional result extraction from response content
                results = await this.processResults(loopResult.content, params, context);
            }
            // Report completion
            if (taskId) {
                this.registry.reportProgress(taskId, 100, 'Complete');
            }
            // ═══════════════════════════════════════════════════════════════════
            // 8. BUILD & RETURN RESULT
            // ═══════════════════════════════════════════════════════════════════
            const result = this.buildFinalResult(results, threadId, loopResult.tokensUsed, summary);
            if (taskId) {
                this.registry.completeTask(taskId, result);
            }
            const totalExecutionTime = performance.now() - executeStartTime;
            console.log(`[${this.name}] ⏱️ TOTAL EXECUTION: ${totalExecutionTime.toFixed(0)}ms`);
            popAgentContext(); // Clean up context on success
            return result;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            // Check if it was a cancellation
            if (errorMessage === 'Task was cancelled') {
                popAgentContext(); // Clean up context
                return this.buildErrorResult('Task was cancelled', threadId);
            }
            // Mark task as failed
            if (taskId) {
                this.registry.failTask(taskId, errorMessage);
            }
            popAgentContext(); // Clean up context on error
            return this.buildErrorResult(errorMessage, threadId);
        }
    }
    /**
     * Build initial messages for the conversation.
     * Override to customize how params become the first user message.
     *
     * Default: Builds a user message from requirements if available.
     *
     * @param params - Agent parameters
     * @param resolvedContext - Optional resolved context items with images
     */
    buildInitialMessages(params, _resolvedContext) {
        // Default: try to build from requirements if available
        if (typeof params === 'object' && params !== null && 'requirements' in params) {
            const requirements = params.requirements;
            if (Array.isArray(requirements)) {
                return [{
                        role: 'user',
                        content: this.buildRequirementsMessage(requirements),
                    }];
            }
        }
        // Fallback: convert params to string
        return [{
                role: 'user',
                content: JSON.stringify(params),
            }];
    }
    /**
     * Build multimodal content parts from resolved context.
     * Extracts text descriptions AND images from context items.
     *
     * Agents call this helper to include images in user messages.
     */
    buildContextContentParts(resolvedContext) {
        const parts = [];
        const resolver = getAgentServices().contextResolver;
        for (const item of resolvedContext) {
            // Add purpose as header (purpose is on the original ContextItem)
            if (item.original?.purpose) {
                parts.push({ type: 'text', text: `\n**${item.original.purpose}**` });
            }
            for (const [contextId, resolved] of item.resolved.entries()) {
                // Add text description
                const description = this.formatContextDescription(contextId, resolved);
                if (description) {
                    parts.push({ type: 'text', text: description });
                }
                // Add associated images
                const imageIds = this.extractContextImageIds(resolved);
                for (const imageId of imageIds) {
                    const img = resolver.getImage(imageId);
                    if (img) {
                        const storedMime = (img.mimeType || '').toLowerCase();
                        const normalizedStoredMime = storedMime === 'image/jpg' ? 'image/jpeg' : storedMime;
                        const detectedMime = detectImageMimeType(img.base64) || undefined;
                        const safeMime = (normalizedStoredMime === 'image/png' || normalizedStoredMime === 'image/jpeg')
                            ? normalizedStoredMime
                            : (detectedMime === 'image/png' || detectedMime === 'image/jpeg'
                                ? detectedMime
                                : null);
                        if (!safeMime) {
                            console.warn(`[${this.name}] Skipping unsupported image mime for context ${imageId}: ` +
                                `${img.mimeType || 'unknown'}`);
                            continue;
                        }
                        parts.push({
                            type: 'image_url',
                            image_url: {
                                url: `data:${safeMime};base64,${img.base64}`,
                                detail: 'auto'
                            }
                        });
                        console.log(`[${this.name}] Added image ${imageId} to context content parts`);
                    }
                }
            }
        }
        return parts;
    }
    /**
     * Extract image IDs from resolved content using conventions:
     * - _imageId: single image
     * - _imageIds: array of images
     */
    extractContextImageIds(resolved) {
        if (!resolved)
            return [];
        const content = resolved.content;
        if (!content || typeof content !== 'object')
            return [];
        const ids = new Set();
        const contentObj = content;
        if ('_imageId' in contentObj && typeof contentObj._imageId === 'string') {
            ids.add(contentObj._imageId);
        }
        if ('_imageIds' in contentObj && Array.isArray(contentObj._imageIds)) {
            for (const id of contentObj._imageIds) {
                if (typeof id === 'string') {
                    ids.add(id);
                }
            }
        }
        return Array.from(ids);
    }
    /**
     * Format a context item as a text description.
     * Override to customize how context appears in messages.
     */
    formatContextDescription(contextId, resolved) {
        if (!resolved)
            return null;
        const content = resolved.content;
        if (!content || typeof content !== 'object')
            return null;
        const contentObj = content;
        // For slide context, return slide index info
        if (contextId.includes('slide_')) {
            const slideIdx = contentObj.slideIndex ?? contentObj.SlideIndex;
            if (slideIdx !== undefined) {
                return `\n[Slide ${slideIdx}] Context ID: ${contextId}`;
            }
        }
        // Generic: return summary/title if available
        const summary = contentObj.summary || contentObj.title || contentObj.description;
        if (summary && typeof summary === 'string') {
            return `\n[${contextId}] ${summary}`;
        }
        return `\n[${contextId}]`;
    }
    /**
     * Process the LLM response and extract structured results.
     * Override to customize how LLM output becomes result items.
     *
     * Default: Returns empty results array.
     */
    async processResults(_responseContent, _params, _context) {
        // Default: return empty results - subclasses should override
        return [];
    }
    /**
     * Hook called before execution starts.
     * Override to perform agent-specific preparation.
     *
     * Default: Does nothing.
     */
    async beforeExecute(_context) {
        // Default: no-op
    }
    /**
     * Hook called when a tool returns a result.
     * Override to capture/process tool results.
     *
     * Default: Does nothing.
     * Can be async for agents that need to fetch additional data (e.g., slide state feedback).
     */
    onToolResult(_toolName, _result, _toolParams) {
        // Default: no-op
    }
    /**
     * Hook called before a tool executes.
     * Override to add preflight checks (e.g., layout gates).
     *
     * Default: Allow tool execution.
     */
    async beforeToolExecute(_context) {
        return { action: "continue" };
    }
    /**
     * Hook called after ALL tools in a batch complete (after parallel tool calls).
     * Override to perform batched operations like capturing slide state feedback
     * once after multiple shapes are created.
     *
     * Default: Does nothing.
     * Can be async.
     *
     * @param _batchResults Array of {toolName, result, params} for all tools in the batch
     */
    onToolBatchComplete(_batchResults) {
        // Default: no-op
    }
    // ═══════════════════════════════════════════════════════════════════════════
    // REMINDER SYSTEM HOOKS
    // ═══════════════════════════════════════════════════════════════════════════
    /**
     * Inject reminders into user messages as SEPARATE CONTENT PARTS.
     *
     * Collects contextual information (presentation state, security warnings, etc.)
     * and wraps each in <non_user_automated_reminder> tags as separate content parts.
     *
     * This follows the Python pattern where each reminder is a separate
     * text content part in the content array, making them easy to strip later.
     *
     * Override to customize what context is collected for this agent.
     *
     * @param messages - Messages to inject reminders into
     * @returns Messages with reminders added to user messages as content arrays
     */
    async injectReminders(messages) {
        const preparer = getAgentServices().messagePreparer;
        const collector = getAgentServices().reminderCollector;
        // Collect context - subclasses can override getReminderOptions()
        const options = this.getReminderOptions();
        const context = await collector.collectContext(options);
        // Add reminders to user messages only
        return messages.map(msg => {
            if (msg.role === 'user') {
                const contentWithReminders = preparer.addRemindersToMessage(msg.content, context, this.name);
                return {
                    ...msg,
                    content: contentWithReminders,
                };
            }
            return msg;
        });
    }
    /**
     * Get reminder collection options for this agent.
     * Override to customize what context is collected.
     *
     * @returns Options for ReminderCollector.collectContext()
     */
    getReminderOptions() {
        return {
            includePresentation: true,
            includeSecurityWarning: true,
            includeCustomInstructions: false,
            includeDeckRegistry: false, // Override in agents that need deck registry
            includeExternalFiles: false, // Override in orchestrator/file search agents
        };
    }
    /**
     * Prepare messages for LLM by stripping old reminders and optionally adding fresh ones.
     *
     * The pattern is:
     * 1. Store FULL messages in thread (with reminders) - for tracking/metadata
     * 2. Before sending to LLM: strip old reminders, keep fresh on last message
     *
     * @param messages - Full message history from thread
     * @param freshContext - Optional fresh context for the last message
     * @returns Cleaned messages ready for LLM
     */
    prepareMessagesForLlm(messages, freshContext) {
        const preparer = getAgentServices().messagePreparer;
        return preparer.prepareMessagesForLlm(messages, freshContext);
    }
    /**
     * Get the tool choice strategy for the next LLM call.
     * Override to dynamically control which tool the LLM must call.
     *
     * @returns Tool choice: 'auto' (default), 'none', or forced function
     *
     * @example
     * ```typescript
     * // Force a specific tool
     * protected getToolChoice(): ToolChoice {
     *     if (this.needsSpecificTool) {
     *         return { type: "function", function: { name: "my_tool" } };
     *     }
     *     return 'auto';
     * }
     * ```
     */
    getToolChoice() {
        return 'auto';
    }
    // ═══════════════════════════════════════════════════════════════════════════
    // TOOL LOOP OPTIONS
    // ═══════════════════════════════════════════════════════════════════════════
    /**
     * Build tool loop options with callback wiring.
     *
     * This wires up all the callbacks that allow the UI (or parent agent) to
     * observe what's happening during tool execution:
     *
     * **Direct tool callbacks:**
     * - `onChunk`: LLM is streaming text
     * - `onToolCall`: Tool is about to be called (args parsed)
     * - `onToolStart`: Tool execution beginning (for progressive UI)
     * - `onOperationField`: Tool argument field parsed during streaming
     * - `onToolResult`: Tool returned a result
     *
     * **Sub-agent callbacks (agent-as-tool pattern):**
     * - `onAgentChunk`: Sub-agent is streaming text
     * - `onAgentToolStart`: Sub-agent's internal tool starting
     * - `onAgentOperationField`: Sub-agent's tool field parsed
     * - `onAgentToolCall`: Sub-agent calling internal tool
     * - `onAgentToolResult`: Sub-agent's tool returned
     *
     * Each callback: logs for debugging → calls agent hook → forwards to UI
     */
    buildToolLoopOptions(systemPrompt, agentCallbacks) {
        const modelSettings = getAgentServices().getModelSettings();
        const modelOverride = modelSettings.chatModel;
        // Inform thread manager of the exact model for context window sizing
        if (this._parentRegistry) {
            this._parentRegistry.threadManager.setModelId(modelOverride);
        }
        return {
            systemPrompt,
            modelOverride,
            maxIterations: this.maxToolLoopIterations,
            // Text streaming
            onChunk: agentCallbacks?.onChunk,
            // Tool lifecycle callbacks (logging moved to timing instrumentation)
            onToolCall: (toolName, args) => {
                agentCallbacks?.onToolCall?.(toolName, args);
            },
            onToolResult: async (toolName, result, toolParams) => {
                // Agent-specific hook (may be async for slide state feedback, etc.)
                await this.onToolResult(toolName, result, toolParams);
                agentCallbacks?.onToolResult?.(toolName, result, toolParams);
            },
            onToolBatchComplete: async (batchResults) => {
                console.log(`[${this.name}] Tool batch complete: ${batchResults.length} tools`);
                // Agent-specific hook for batched operations (e.g., single feedback after multiple shapes)
                await this.onToolBatchComplete(batchResults);
            },
            onToolStart: (toolName, hasStreaming, isAgent) => {
                agentCallbacks?.onToolStart?.(toolName, hasStreaming, isAgent);
            },
            onOperationField: (toolName, fieldName, value) => {
                agentCallbacks?.onOperationField?.(toolName, fieldName, value);
            },
            // Sub-agent callbacks (for agent-as-tool pattern)
            onAgentChunk: (toolName, chunk) => {
                agentCallbacks?.onAgentChunk?.(toolName, chunk);
            },
            onAgentToolStart: (agentName, internalToolName, hasOperation, isAgent) => {
                agentCallbacks?.onAgentToolStart?.(agentName, internalToolName, hasOperation, isAgent);
            },
            onAgentOperationField: (agentName, internalToolName, fieldName, value) => {
                agentCallbacks?.onAgentOperationField?.(agentName, internalToolName, fieldName, value);
            },
            onAgentToolCall: (agentName, internalToolName, args) => {
                agentCallbacks?.onAgentToolCall?.(agentName, internalToolName, args);
            },
            onAgentToolResult: (agentName, internalToolName, result) => {
                agentCallbacks?.onAgentToolResult?.(agentName, internalToolName, result);
            },
        };
    }
    /**
     * Build the final result from processed results.
     * Override to customize the result structure.
     *
     * Default: Uses buildResult or buildPartialResult based on results count.
     */
    buildFinalResult(results, threadId, tokensUsed, summary) {
        if (results.length === 0) {
            return this.buildPartialResult(results, threadId, tokensUsed, summary);
        }
        return this.buildResult(results, threadId, tokensUsed, summary);
    }
    /**
     * Get the list of tools available to this agent
     */
    getTools() {
        return this.toolCalls;
    }
    /**
     * Add a tool to this agent
     */
    addTool(tool) {
        this.toolCalls.push(tool);
    }
    /**
     * Set the tools for this agent
     */
    setTools(tools) {
        this.toolCalls = tools;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // PARAMETER PREPROCESSING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Pre-process parameters to handle LLM stringification issues.
     *
     * Some LLMs (especially Claude) sometimes return stringified JSON for
     * nested objects/arrays, e.g., `"[{...}]"` instead of `[{...}]`.
     * This method attempts to parse those back into proper objects.
     */
    preprocessParameters(params) {
        if (!params || typeof params !== 'object') {
            return params;
        }
        const processed = {};
        for (const [key, value] of Object.entries(params)) {
            if (typeof value === 'string') {
                // Try to parse stringified JSON
                const trimmed = value.trim();
                if ((trimmed.startsWith('[') && trimmed.endsWith(']')) ||
                    (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
                    try {
                        processed[key] = JSON.parse(trimmed);
                        console.log(`[${this.name}] Parsed stringified ${key}:`, processed[key]);
                        continue;
                    }
                    catch {
                        // Not valid JSON, keep as string
                    }
                }
                // Handle stringified booleans
                if (trimmed === 'true') {
                    processed[key] = true;
                    console.log(`[${this.name}] Converted string 'true' to boolean for ${key}`);
                    continue;
                }
                if (trimmed === 'false') {
                    processed[key] = false;
                    console.log(`[${this.name}] Converted string 'false' to boolean for ${key}`);
                    continue;
                }
            }
            processed[key] = value;
        }
        return processed;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // RESULT BUILDING (for agent-as-tool pattern)
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Build a standardized success result
     */
    buildResult(results, threadId, tokensUsed, summary) {
        if (!this.useThreadManagement) {
            throw new Error('Thread management must be enabled to build results');
        }
        const threadContext = this.threadManager.buildThreadContext(threadId, tokensUsed);
        const result = {
            results,
            status: 'success',
            ...threadContext,
        };
        // Include summary if provided
        if (summary) {
            result.summary = summary;
        }
        return result;
    }
    /**
     * Build a partial success result (some requirements fulfilled)
     */
    buildPartialResult(results, threadId, tokensUsed, summary) {
        if (!this.useThreadManagement) {
            throw new Error('Thread management must be enabled to build results');
        }
        const threadContext = this.threadManager.buildThreadContext(threadId, tokensUsed);
        const result = {
            results,
            status: 'partial',
            ...threadContext,
        };
        // Include summary if provided
        if (summary) {
            result.summary = summary;
        }
        return result;
    }
    /**
     * Build an error result
     */
    buildErrorResult(error, threadId) {
        const actualThreadId = threadId ||
            (this.agentType ? `${this.agentType}_thread_error` : 'unknown_thread_error');
        return {
            results: [],
            status: 'error',
            error,
            threadId: actualThreadId,
            threadContextUsed: 0,
            threadContextUsedTokens: 0,
            threadContextMaxTokens: 0,
        };
    }
    // ═══════════════════════════════════════════════════════════════════════
    // UTILITIES
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Build a message from requirements
     */
    buildRequirementsMessage(requirements) {
        const lines = requirements.map(r => `[${r.id}] ${r.requirement}`);
        return `Please address the following requirements:\n\n${lines.join('\n')}`;
    }
    /**
     * Store content and get a short ID
     */
    storeResult(prefix, content) {
        if (!this.useContextResolution) {
            throw new Error('Context resolution must be enabled to store results');
        }
        return this.contextResolver.store(prefix, content);
    }
    /**
     * Store an image in the cache (sync, no resizing)
     *
     * @deprecated Use storeImageAsync for automatic resizing
     */
    storeImage(id, base64, mimeType, metadata) {
        if (!this.useContextResolution) {
            throw new Error('Context resolution must be enabled to store images');
        }
        this.contextResolver.storeImage(id, base64, mimeType, metadata);
    }
    /**
     * Store an image in the cache with automatic resizing
     *
     * Automatically resizes images that exceed LLM limits:
     * - Hard limit: 8000px (or 2000px for >20 images with Claude)
     * - Optimal: 1568px, 1.15 megapixels
     */
    async storeImageAsync(id, base64, mimeType, metadata) {
        if (!this.useContextResolution) {
            throw new Error('Context resolution must be enabled to store images');
        }
        return this.contextResolver.storeImageAsync(id, base64, mimeType, metadata);
    }
    // ═══════════════════════════════════════════════════════════════════════
    // TOOL LOOP - Shared by all agents
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Run the multi-turn tool loop.
     *
     * Handles both streaming and non-streaming modes:
     * - Streaming: Process chunks incrementally, accumulate tool calls
     * - Non-streaming: Get complete response, parse tool calls at once
     *
     * @param messages - Initial messages to send
     * @param options - Tool loop options
     * @returns Result with final content and messages
     */
    async runToolLoop(messages, options) {
        const services = getAgentServices();
        const llmService = services.getLLMService();
        const workingMessages = [...messages];
        let iteration = 0;
        let tokensUsed = 0;
        let llmRetries = 0;
        const MAX_LLM_RETRIES = 2;
        const maxIterations = options.maxIterations ?? this.maxToolLoopIterations;
        while (iteration < maxIterations) {
            // Check cancellation at the start of each iteration
            if (this.currentCancellationToken?.isCancelled) {
                console.log(`[${this.name}] 🛑 Cancellation detected at iteration ${iteration}`);
                return {
                    content: 'Task was cancelled',
                    messages: workingMessages,
                    tokensUsed,
                    completed: false,
                    error: 'Task was cancelled',
                };
            }
            iteration++;
            const iterationStartTime = performance.now();
            console.log(`[${this.name}] ⏱️ ITERATION ${iteration}/${maxIterations} START`);
            // ═══════════════════════════════════════════════════════════════════════
            // CHECKPOINT CAPTURE BEFORE LLM CALL (for replay functionality)
            // ═══════════════════════════════════════════════════════════════════════
            // Capture PPT state BEFORE the first LLM call for agents that modify PPT.
            // This enables "Replay from here" to restore PPT to the exact state at this turn.
            // Only capture on first iteration to avoid multiple checkpoints per tool loop.
            if (this.createsUndoCheckpoint && iteration === 1) {
                const checkpointManager = getAgentServices().checkpointManager;
                const sessionLogger = getAgentServices().logger;
                if (!checkpointManager?.captureCheckpoint || !sessionLogger?.logError) {
                    console.warn(`[${this.name}] Checkpoint capture service unavailable; skipping pre-LLM checkpoint`);
                }
                else {
                    const turnNumber = (sessionLogger.getCurrentTurnNumber?.() || 0) + 1; // Next turn (this call)
                    // Fire and forget - don't block on checkpoint capture
                    checkpointManager.captureCheckpoint(turnNumber, this.name)
                        .then((checkpoint) => {
                        if (!checkpoint) {
                            sessionLogger.logError?.('checkpoint_capture_failed', 'Pre-LLM checkpoint capture returned null', {
                                phase: 'pre_llm',
                                agentName: this.name,
                                turnNumber,
                            });
                        }
                    })
                        .catch((err) => {
                        console.warn(`[${this.name}] Pre-LLM checkpoint capture failed:`, err);
                        sessionLogger.logError?.('checkpoint_capture_failed', err instanceof Error ? err : String(err), {
                            phase: 'pre_llm',
                            agentName: this.name,
                            turnNumber,
                        });
                    });
                }
            }
            let response = '';
            let toolCalls = [];
            try {
                const llmStartTime = performance.now();
                // ═══════════════════════════════════════════════════════════════
                // PREPARE MESSAGES FOR LLM (strip old reminders, keep last fresh)
                // This MUST be called before EACH LLM iteration because:
                // - Feedback messages with reminders are added during the tool loop
                // - Without re-stripping, old reminder tags accumulate in context
                // ═══════════════════════════════════════════════════════════════
                const preparedMessages = this.prepareMessagesForLlm(workingMessages);
                // Filter out empty messages that may result from stripping all reminder content
                // An empty message (empty string or empty array) would cause API errors
                const filteredMessages = preparedMessages.filter(msg => {
                    // Assistant tool-call envelopes must be preserved even when content is null/empty.
                    // Dropping these would orphan subsequent tool_result messages.
                    if (msg.role === 'assistant' && Array.isArray(msg.tool_calls) && msg.tool_calls.length > 0) {
                        return true;
                    }
                    if (typeof msg.content === 'string') {
                        return msg.content.trim().length > 0;
                    }
                    if (Array.isArray(msg.content)) {
                        return msg.content.length > 0;
                    }
                    // Tool messages with no content are valid (they have tool_call_id)
                    if (msg.role === 'tool') {
                        return true;
                    }
                    return msg.content !== undefined && msg.content !== null;
                });
                if (filteredMessages.length !== workingMessages.length) {
                    console.log(`[${this.name}] 🧹 Filtered out ${workingMessages.length - filteredMessages.length} empty messages after stripping reminders`);
                }
                if (this.stream) {
                    // STREAMING MODE
                    const result = await this.processStreamingResponse(llmService, filteredMessages, options, iteration);
                    response = result.response;
                    toolCalls = result.toolCalls;
                    tokensUsed = result.tokensUsed;
                }
                else {
                    // NON-STREAMING MODE
                    const result = await this.processCompleteResponse(llmService, filteredMessages, options, iteration);
                    response = result.response;
                    toolCalls = result.toolCalls;
                    tokensUsed = result.tokensUsed;
                }
                const llmTime = performance.now() - llmStartTime;
                console.log(`[${this.name}] ⏱️ LLM RESPONSE: ${llmTime.toFixed(0)}ms (${toolCalls.filter(tc => tc.name).length} tool calls)`);
                llmRetries = 0; // Reset on success
            }
            catch (error) {
                const errorMsg = error instanceof Error ? error.message : String(error);
                const isTransient = /status.*(500|502|503|529)|network|ECONNRESET|ETIMEDOUT|Failed to parse tool call|Unexpected end of JSON/i.test(errorMsg);
                if (isTransient && llmRetries < MAX_LLM_RETRIES) {
                    llmRetries++;
                    const delayMs = 2000 * llmRetries;
                    console.warn(`[${this.name}] Transient API error (attempt ${llmRetries}/${MAX_LLM_RETRIES}), retrying in ${delayMs}ms: ${errorMsg}`);
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                    continue;
                }
                console.error(`[${this.name}] Error in tool loop:`, error);
                return {
                    content: response,
                    messages: workingMessages,
                    tokensUsed,
                    completed: false,
                    error: errorMsg,
                };
            }
            // Filter valid tool calls
            const validToolCalls = toolCalls.filter(tc => tc.name && tc.id);
            // If no tool calls, we're done - but FIRST add the assistant message to preserve conversation history!
            if (validToolCalls.length === 0) {
                // BUG FIX: Must add assistant's text-only response to workingMessages
                // Otherwise, responses without tool calls are lost from conversation history
                workingMessages.push({
                    role: 'assistant',
                    content: response,
                });
                const iterationTime = performance.now() - iterationStartTime;
                console.log(`[${this.name}] ⏱️ ITERATION ${iteration} COMPLETE (no tools): ${iterationTime.toFixed(0)}ms`);
                return {
                    content: response,
                    messages: workingMessages,
                    tokensUsed,
                    completed: true,
                };
            }
            // Track if submitResults was called this iteration
            let submitResultsData;
            // Add assistant message with tool calls
            workingMessages.push({
                role: 'assistant',
                content: response || null,
                tool_calls: validToolCalls.map(tc => ({
                    id: tc.id,
                    type: 'function',
                    function: { name: tc.name, arguments: tc.arguments },
                })),
            });
            // Execute tool calls with MessageBuilder for image handling
            const messageBuilder = getAgentServices().messageBuilder;
            // Collect batch results for onToolBatchComplete callback
            const batchResults = [];
            for (const toolCall of validToolCalls) {
                // Check cancellation before each tool execution
                if (this.currentCancellationToken?.isCancelled) {
                    console.log(`[${this.name}] 🛑 Cancellation detected before tool ${toolCall.name}`);
                    return {
                        content: 'Task was cancelled',
                        messages: workingMessages,
                        tokensUsed,
                        completed: false,
                        error: 'Task was cancelled',
                    };
                }
                const toolStartTime = performance.now();
                // Session logging: parse tool args once before execution.
                const toolLogger = getAgentServices().logger;
                const tool = this.toolCalls.find(t => t.name === toolCall.name);
                let parsedArgs;
                try {
                    const rawArgs = JSON.parse(toolCall.arguments);
                    if (!rawArgs || typeof rawArgs !== 'object' || Array.isArray(rawArgs)) {
                        throw new Error('Tool arguments must be a JSON object');
                    }
                    parsedArgs = rawArgs;
                }
                catch (parseError) {
                    const parseErrorMsg = parseError instanceof Error ? parseError.message : String(parseError);
                    const errorMsg = `Invalid tool arguments JSON: ${parseErrorMsg}`;
                    console.warn(`[${this.name}] Skipping tool ${toolCall.name}; ${errorMsg}`);
                    toolLogger.logToolError(this.name, toolCall.name, errorMsg, toolCall.id, undefined);
                    workingMessages.push({
                        role: 'tool',
                        tool_call_id: toolCall.id,
                        content: JSON.stringify({ error: errorMsg }),
                    });
                    continue;
                }
                options.onToolCall?.(toolCall.name, toolCall.arguments);
                toolLogger.logToolStart(this.name, toolCall.name, parsedArgs, toolCall.id, tool?.isAgent);
                // ═══════════════════════════════════════════════════════════════
                // CHECKPOINT CAPTURE (for admin panel replay/debugging)
                // ═══════════════════════════════════════════════════════════════
                // Capture PPT state after tool_start turn is logged and BEFORE execution.
                // This ties the checkpoint to the tool_start node in the session log.
                const shouldCaptureCheckpoint = this.shouldCaptureCheckpointForTool(toolCall.name);
                if (shouldCaptureCheckpoint) {
                    try {
                        const checkpointManager = getAgentServices().checkpointManager;
                        if (!checkpointManager?.captureCheckpoint) {
                            console.warn(`[${this.name}] Checkpoint capture service unavailable; skipping tool checkpoint for ${toolCall.name}`);
                        }
                        else {
                            const checkpointTurnNumber = toolLogger.getCurrentTurnNumber?.() || 0;
                            const checkpoint = await checkpointManager.captureCheckpoint(checkpointTurnNumber, toolCall.name);
                            if (!checkpoint) {
                                toolLogger.logError('checkpoint_capture_failed', `Checkpoint capture returned null for tool ${toolCall.name}`, {
                                    phase: 'before_tool_execute',
                                    agentName: this.name,
                                    toolName: toolCall.name,
                                    turnNumber: checkpointTurnNumber,
                                });
                            }
                        }
                    }
                    catch (err) {
                        console.warn(`[${this.name}] Checkpoint capture failed:`, err);
                        toolLogger.logError('checkpoint_capture_failed', err instanceof Error ? err : String(err), {
                            phase: 'before_tool_execute',
                            agentName: this.name,
                            toolName: toolCall.name,
                            turnNumber: toolLogger.getCurrentTurnNumber?.() || 0,
                        });
                    }
                }
                try {
                    if (!tool) {
                        throw new Error(`Tool not found: ${toolCall.name}`);
                    }
                    const args = parsedArgs;
                    const preflight = await this.beforeToolExecute({
                        toolCall,
                        tool,
                        args,
                    });
                    if (preflight.action === "block") {
                        const toolTime = performance.now() - toolStartTime;
                        console.log(`[${this.name}] ⛔ TOOL ${toolCall.name} blocked by preflight: ${toolTime.toFixed(0)}ms`);
                        if (preflight.haltToolLoop) {
                            this._pendingPreflightRetry = {
                                toolCall,
                                args,
                                tool,
                            };
                        }
                        await options.onToolResult?.(toolCall.name, preflight.result, args);
                        toolLogger.logToolComplete(this.name, toolCall.name, preflight.result, toolCall.id);
                        workingMessages.push(messageBuilder.buildToolResultMessage(toolCall.id, preflight.result));
                        if (preflight.haltToolLoop) {
                            break;
                        }
                        continue;
                    }
                    // For agents (AgentBaseClass with isAgent=true), pass callbacks to forward streaming text
                    let result;
                    if (tool.isAgent) {
                        // ═══════════════════════════════════════════════════════════════
                        // SUB-AGENT THREAD SWITCHING
                        // ═══════════════════════════════════════════════════════════════
                        // Save current thread so we can switch back after sub-agent returns
                        const parentThreadId = this.useThreadManagement
                            ? this.threadManager.getActiveThreadId()
                            : null;
                        // Determine sub-agent's thread:
                        // - If args.threadId passed: continue that thread (orchestrator's decision)
                        // - Otherwise: create new thread for fresh context
                        let subAgentThreadId = args.threadId;
                        if (!subAgentThreadId && this.useThreadManagement) {
                            subAgentThreadId = this.threadManager.createThread({
                                agentType: tool.name,
                                conversationId: this.currentConversationId || 'default',
                                documentId: this.currentdocumentId || 'default',
                            }, /* setActive */ false);
                            // Pass the new threadId to the sub-agent
                            args.threadId = subAgentThreadId;
                        }
                        // Set sub-agent's thread as active BEFORE calling
                        if (this.useThreadManagement && subAgentThreadId) {
                            this.threadManager.setActiveThread(subAgentThreadId);
                            console.log(`[${this.name}] Switched active thread to sub-agent: ${subAgentThreadId}`);
                        }
                        // ═══════════════════════════════════════════════════════════════
                        // INJECT conversationId, documentId, and main conversation threadId INTO SUB-AGENT ARGS
                        // ═══════════════════════════════════════════════════════════════
                        // This ensures sub-agents have proper context for thread isolation and tracing
                        if (this.currentConversationId) {
                            args.conversationId = this.currentConversationId;
                        }
                        if (this.currentdocumentId) {
                            args.documentId = this.currentdocumentId;
                        }
                        console.log(`[${this.name}] 🔗 Passing context to sub-agent ${toolCall.name}:`);
                        console.log(`[${this.name}]    conversationId: ${args.conversationId || 'MISSING'}`);
                        console.log(`[${this.name}]    documentId: ${args.documentId || 'MISSING'}`);
                        console.log(`[${this.name}]    threadId: ${args.threadId || 'MISSING'}`);
                        // Pass callbacks so agent's internal streaming and tool calls are forwarded
                        // Use toolCall.name (the sub-agent name) as agentName so UI can find the toolData entry
                        // For nested sub-agents (Level 2+), forward onAgent* callbacks with the current sub-agent's name
                        result = await tool.execute(args, {
                            // CANCELLATION: Pass our token to sub-agent for cooperative cancellation
                            parentCancellationToken: this.currentCancellationToken,
                            onChunk: (chunk) => {
                                options.onAgentChunk?.(toolCall.name, chunk);
                            },
                            // When sub-agent calls a tool, report with toolCall.name so UI finds correct toolData
                            onToolStart: (subToolName, hasOperation, subIsAgent) => {
                                console.log(`[${this.name}] Sub-agent ${toolCall.name} tool starting: ${subToolName}, isAgent: ${subIsAgent}`);
                                options.onAgentToolStart?.(toolCall.name, subToolName, hasOperation, subIsAgent);
                            },
                            onOperationField: (subToolName, fieldName, value) => {
                                console.log(`[${this.name}] Sub-agent ${toolCall.name} field parsed: ${subToolName}.${fieldName}`);
                                options.onAgentOperationField?.(toolCall.name, subToolName, fieldName, value);
                            },
                            onToolCall: (subToolName, subArgs) => {
                                console.log(`[${this.name}] Sub-agent ${toolCall.name} calling tool: ${subToolName}`);
                                const argsStr = typeof subArgs === 'string' ? subArgs : JSON.stringify(subArgs);
                                options.onAgentToolCall?.(toolCall.name, subToolName, argsStr);
                            },
                            onToolResult: (subToolName, subResult) => {
                                console.log(`[${this.name}] Sub-agent ${toolCall.name} tool result from: ${subToolName}`);
                                options.onAgentToolResult?.(toolCall.name, subToolName, subResult);
                            },
                            // Forward nested agent callbacks (for deep nesting like orchestrator > linkedExcelEditorAgent > editChartAgent)
                            // Use toolCall.name so all nested calls are attributed to the correct first-level agent
                            onAgentChunk: (nestedAgentName, chunk) => {
                                options.onAgentChunk?.(`${toolCall.name}/${nestedAgentName}`, chunk);
                            },
                            onAgentToolStart: (nestedAgentName, nestedToolName, hasOperation, nestedIsAgent) => {
                                // Prefix tool name with nested path for clarity
                                const prefixedToolName = `${nestedAgentName}/${nestedToolName}`;
                                console.log(`[${this.name}] Nested agent ${toolCall.name}/${nestedAgentName} tool starting: ${nestedToolName}, isAgent: ${nestedIsAgent}`);
                                options.onAgentToolStart?.(toolCall.name, prefixedToolName, hasOperation, nestedIsAgent);
                            },
                            onAgentOperationField: (nestedAgentName, nestedToolName, fieldName, value) => {
                                const prefixedToolName = `${nestedAgentName}/${nestedToolName}`;
                                console.log(`[${this.name}] Nested agent ${toolCall.name}/${nestedAgentName} field parsed: ${nestedToolName}.${fieldName}`);
                                options.onAgentOperationField?.(toolCall.name, prefixedToolName, fieldName, value);
                            },
                            onAgentToolCall: (nestedAgentName, nestedToolName, nestedArgs) => {
                                const prefixedToolName = `${nestedAgentName}/${nestedToolName}`;
                                console.log(`[${this.name}] Nested agent ${toolCall.name}/${nestedAgentName} calling tool: ${nestedToolName}`);
                                options.onAgentToolCall?.(toolCall.name, prefixedToolName, nestedArgs);
                            },
                            onAgentToolResult: (nestedAgentName, nestedToolName, nestedResult) => {
                                const prefixedToolName = `${nestedAgentName}/${nestedToolName}`;
                                console.log(`[${this.name}] Nested agent ${toolCall.name}/${nestedAgentName} tool result from: ${nestedToolName}`);
                                options.onAgentToolResult?.(toolCall.name, prefixedToolName, nestedResult);
                            },
                        });
                        // Switch back to parent's thread AFTER sub-agent returns
                        if (this.useThreadManagement && parentThreadId) {
                            this.threadManager.setActiveThread(parentThreadId);
                            console.log(`[${this.name}] Switched active thread back to parent: ${parentThreadId}`);
                        }
                    }
                    else {
                        result = await tool.execute(args);
                    }
                    const toolTime = performance.now() - toolStartTime;
                    console.log(`[${this.name}] ⏱️ TOOL ${toolCall.name}: ${toolTime.toFixed(0)}ms`);
                    // Await onToolResult in case it's async (e.g., for slide state feedback)
                    await options.onToolResult?.(toolCall.name, result, args);
                    // Collect for batch callback (called after ALL tools in this batch complete)
                    batchResults.push({ toolName: toolCall.name, result, params: args });
                    // Session logging: log tool complete
                    toolLogger.logToolComplete(this.name, toolCall.name, result, toolCall.id);
                    // Extract and queue any images from the result BEFORE checking submitResults
                    // This ensures images are captured even when agent is finishing
                    // Only queue if injectImagesForVision is enabled
                    if (this.injectImagesForVision) {
                        const imageIds = extractImageIds(result);
                        if (imageIds.length > 0) {
                            messageBuilder.queueImages(imageIds);
                            console.log(`[${this.name}] Queued ${imageIds.length} images for injection:`, imageIds);
                        }
                    }
                    // Check if this is a submitResults call (Submit Results Pattern)
                    if (this.useSubmitResultsPattern && isSubmitResultsResponse(result)) {
                        submitResultsData = result.submittedResults;
                        console.log(`[${this.name}] submitResults called with ${submitResultsData.results.length} results`);
                        // Add tool result message before exiting
                        workingMessages.push({
                            role: 'tool',
                            tool_call_id: toolCall.id,
                            content: JSON.stringify({ accepted: true, message: result.message }),
                        });
                        // Don't process more tool calls - we're done
                        break;
                    }
                    // Add tool result message (metadata only, images injected later)
                    workingMessages.push(messageBuilder.buildToolResultMessage(toolCall.id, result));
                    if (toolCall.name === 'layoutMistakeStopper') {
                        const continueFlag = result?.continue;
                        const pendingRetry = this._pendingPreflightRetry;
                        this._pendingPreflightRetry = undefined;
                        if (continueFlag === true && pendingRetry) {
                            const retryId = generateId('toolcall');
                            const retryArgs = pendingRetry.args;
                            const retryTool = pendingRetry.tool;
                            const retryToolCall = {
                                id: retryId,
                                name: pendingRetry.toolCall.name,
                                arguments: JSON.stringify(retryArgs),
                            };
                            options.onToolCall?.(retryToolCall.name, retryToolCall.arguments);
                            toolLogger.logToolStart(this.name, retryToolCall.name, retryArgs, retryToolCall.id, retryTool.isAgent);
                            workingMessages.push({
                                role: 'assistant',
                                content: null,
                                tool_calls: [{
                                        id: retryToolCall.id,
                                        type: 'function',
                                        function: {
                                            name: retryToolCall.name,
                                            arguments: retryToolCall.arguments,
                                        },
                                    }],
                            });
                            try {
                                const retryPreflight = await this.beforeToolExecute({
                                    toolCall: retryToolCall,
                                    tool: retryTool,
                                    args: retryArgs,
                                });
                                if (retryPreflight.action === "block") {
                                    toolLogger.logToolComplete(this.name, retryToolCall.name, retryPreflight.result, retryToolCall.id);
                                    workingMessages.push(messageBuilder.buildToolResultMessage(retryToolCall.id, retryPreflight.result));
                                }
                                else {
                                    const retryResult = await retryTool.execute(retryArgs);
                                    await options.onToolResult?.(retryToolCall.name, retryResult, retryArgs);
                                    toolLogger.logToolComplete(this.name, retryToolCall.name, retryResult, retryToolCall.id);
                                    workingMessages.push(messageBuilder.buildToolResultMessage(retryToolCall.id, retryResult));
                                }
                            }
                            catch (retryError) {
                                const retryErrorMsg = retryError instanceof Error ? retryError.message : String(retryError);
                                toolLogger.logToolError(this.name, retryToolCall.name, retryError instanceof Error ? retryError : retryErrorMsg, retryToolCall.id, retryArgs);
                                workingMessages.push({
                                    role: 'tool',
                                    tool_call_id: retryToolCall.id,
                                    content: JSON.stringify({ error: retryErrorMsg }),
                                });
                            }
                        }
                    }
                }
                catch (error) {
                    const errorMsg = error instanceof Error ? error.message : String(error);
                    console.error(`[${this.name}] Tool execution error:`, error);
                    // Session logging: log tool error
                    toolLogger.logToolError(this.name, toolCall.name, error instanceof Error ? error : String(error), toolCall.id, parsedArgs);
                    workingMessages.push({
                        role: 'tool',
                        tool_call_id: toolCall.id,
                        content: JSON.stringify({ error: errorMsg }),
                    });
                }
            }
            // Call batch complete callback after ALL tools in this iteration are done
            // This allows agents to batch operations like slide state feedback
            if (batchResults.length > 0 && options.onToolBatchComplete) {
                await options.onToolBatchComplete(batchResults);
            }
            // After all tool results: inject pending feedback messages
            // These are used by ExecutionAgent for slide state feedback after buildArea calls
            // MUST happen BEFORE image injection and submitResults check
            if (messageBuilder.hasPendingFeedbackMessages()) {
                const feedbackMessages = messageBuilder.getPendingFeedbackMessages();
                for (const feedback of feedbackMessages) {
                    workingMessages.push(feedback.message);
                    console.log(`[${this.name}] Injected feedback message from ${feedback.source}`);
                }
            }
            // After all tool results: inject pending images in a user message
            // MUST happen BEFORE submitResults check so LLM can see images even when finishing
            if (messageBuilder.getPendingImageCount() > 0) {
                const pendingCount = messageBuilder.getPendingImageCount();
                // Build a message that explicitly asks the agent to visually confirm the images
                const imageMessage = messageBuilder.buildUserMessage(`Please visually confirm that each of the ${pendingCount} retrieved image(s) matches the user's request. Analyze each image carefully and verify it shows the correct content (e.g., correct company logo, correct person, correct product). If any image doesn't match, you should try a different result from the search or search again with refined terms.`);
                workingMessages.push(imageMessage);
                console.log(`[${this.name}] Injected ${pendingCount} images into user message with visual confirmation instruction`);
            }
            // If submitResults was called, exit the loop with those results
            if (submitResultsData) {
                const iterationTime = performance.now() - iterationStartTime;
                console.log(`[${this.name}] ⏱️ ITERATION ${iteration} COMPLETE (submitted): ${iterationTime.toFixed(0)}ms`);
                return {
                    content: response,
                    messages: workingMessages,
                    tokensUsed,
                    completed: true,
                    submittedResults: submitResultsData,
                };
            }
            const iterationTime = performance.now() - iterationStartTime;
            console.log(`[${this.name}] ⏱️ ITERATION ${iteration} COMPLETE: ${iterationTime.toFixed(0)}ms`);
        }
        // Max iterations reached
        return {
            content: 'Max iterations reached without final response',
            messages: workingMessages,
            tokensUsed,
            completed: false,
            error: 'Max iterations reached',
        };
    }
    /**
     * Process LLM response in streaming mode
     */
    async processStreamingResponse(llmService, messages, options, iteration) {
        // Log request details for debugging
        this.logLLMRequest(messages, options.systemPrompt, iteration);
        // Session logging: log LLM request with full payload for replay
        const sessionLogger = getAgentServices().logger;
        const modelSettings = getAgentServices().getModelSettings();
        sessionLogger.logLLMRequest(this.name, messages.length, this.toolCalls.length, modelSettings?.chatModel, options.systemPrompt?.length, {
            systemPrompt: options.systemPrompt,
            messages: messages,
            toolChoice: 'auto',
            maxTokens: this.maxResponseTokens,
            extraBody: this.getKeywordsExtraBody(iteration),
        });
        const toolChoice = options.toolChoice ?? "auto";
        const extraBody = options.extraBody ?? this.getKeywordsExtraBody(iteration);
        const streamResult = await consumeStreamingToolCalls(llmService.createLLMResponseStream(messages, options.modelOverride, {
            system: options.systemPrompt,
            tools: this.toolCalls,
            tool_choice: toolChoice,
            max_tokens: this.maxResponseTokens,
            temperature: options.temperature,
            extra_body: extraBody,
            extra_headers: options.extraHeaders,
            thinking: options.thinking,
        }), {
            resolveTool: (toolName) => {
                const tool = this.toolCalls.find((candidate) => candidate.name === toolName);
                if (!tool)
                    return undefined;
                return {
                    name: tool.name,
                    fieldsToCheck: tool.fieldsToCheck,
                    listsToCheck: tool.listsToCheck,
                    streamableOperation: hasStreamingSupport(tool) ? tool : undefined,
                    isAgentTool: tool.isAgent,
                };
            },
            callbacks: {
                onTextDelta: (delta) => {
                    options.onChunk?.(delta);
                },
                onToolStart: (toolName, hasStreaming, isAgentTool) => {
                    options.onToolStart?.(toolName, hasStreaming, isAgentTool);
                },
                onOperationField: (toolName, fieldName, value) => {
                    options.onOperationField?.(toolName, fieldName, value);
                },
                onParseError: ({ toolName, callIndex, error }) => {
                    console.warn(`[${this.name}] Streaming parse error (tool=${toolName}, index=${callIndex}): ${error}`);
                },
            },
        });
        const response = streamResult.assistantText;
        const toolCalls = streamResult.toolCalls.map((call) => ({
            id: call.id,
            name: call.name,
            arguments: call.arguments,
        }));
        let tokensUsed = streamResult.tokensUsed;
        if (tokensUsed === 0) {
            tokensUsed = Math.ceil(response.length / 4) + 100;
        }
        sessionLogger.logLLMResponse(this.name, response, tokensUsed, toolCalls.map((tc) => ({ id: tc.id, name: tc.name, arguments: tc.arguments })), toolCalls.length > 0 ? "tool_calls" : "stop");
        return { response, toolCalls, tokensUsed };
    }
    /**
     * Process LLM response in non-streaming mode
     */
    async processCompleteResponse(llmService, messages, options, iteration) {
        // Log request details for debugging
        this.logLLMRequest(messages, options.systemPrompt, iteration);
        // Session logging: log LLM request with full payload for replay
        const sessionLogger = getAgentServices().logger;
        const modelSettings = getAgentServices().getModelSettings();
        sessionLogger.logLLMRequest(this.name, messages.length, this.toolCalls.length, modelSettings?.chatModel, options.systemPrompt?.length, {
            systemPrompt: options.systemPrompt,
            messages: messages,
            toolChoice: 'auto',
            maxTokens: this.maxResponseTokens,
            extraBody: this.getKeywordsExtraBody(iteration),
        });
        const toolChoice = options.toolChoice ?? 'auto';
        const extraBody = options.extraBody ?? this.getKeywordsExtraBody(iteration);
        const result = await llmService.createLLMResponseComplete(messages, options.modelOverride, {
            system: options.systemPrompt,
            tools: this.toolCalls,
            tool_choice: toolChoice,
            max_tokens: this.maxResponseTokens,
            temperature: options.temperature,
            extra_body: extraBody,
            extra_headers: options.extraHeaders,
            thinking: options.thinking,
        });
        const choice = result.choices?.[0];
        // Extract text content from response (handles both string and ContentBlock[] formats)
        const response = extractTextContent(choice?.message?.content);
        // Get actual token usage from LLM response
        const tokensUsed = result.usage?.total_tokens || Math.ceil(response.length / 4) + 100;
        // Send full response to onChunk for consistency
        if (response && options.onChunk) {
            options.onChunk(response);
        }
        // Extract tool calls and commit to operations
        const toolCalls = [];
        if (choice?.message?.tool_calls) {
            for (const tc of choice.message.tool_calls) {
                toolCalls.push({
                    id: tc.id,
                    name: tc.function.name,
                    arguments: tc.function.arguments,
                });
                // Find tool and commit all fields at once to operation
                const tool = this.toolCalls.find(t => t.name === tc.function.name);
                // Check if tool supports streaming (ToolBase pattern)
                let hasStreaming = false;
                let operation;
                if (hasStreamingSupport(tool)) {
                    // ToolBase pattern - tool IS the operation
                    hasStreaming = true;
                    operation = tool;
                }
                if (operation && hasStreaming) {
                    // Notify UI that tool is starting
                    options.onToolStart?.(tc.function.name, true, tool.isAgent);
                    // Reset and commit all fields
                    operation.reset();
                    try {
                        const args = JSON.parse(tc.function.arguments);
                        operation.commitAll(args);
                        // Fire field callbacks for each committed field
                        for (const [fieldName, value] of Object.entries(operation.getExtractedData())) {
                            options.onOperationField?.(tc.function.name, fieldName, value);
                        }
                    }
                    catch (e) {
                        console.warn(`[${this.name}] Failed to parse tool arguments for operation:`, e);
                    }
                }
                else {
                    // No streaming support, just fire onToolStart
                    options.onToolStart?.(tc.function.name, false, tool?.isAgent);
                }
            }
        }
        // Session logging: log LLM response
        sessionLogger.logLLMResponse(this.name, response, tokensUsed, toolCalls.map(tc => ({ id: tc.id, name: tc.name, arguments: tc.arguments })), toolCalls.length > 0 ? 'tool_calls' : 'stop');
        return { response, toolCalls, tokensUsed };
    }
    // ═══════════════════════════════════════════════════════════════════════════
    // REPLAY FROM LOG
    // ═══════════════════════════════════════════════════════════════════════════
    /**
     * Replay a conversation from a log with pre-built messages.
     *
     * This method allows running the agent with existing messages (from a log replay
     * or continued chat session) instead of building messages from params.
     *
     * Key differences from execute():
     * - Uses provided messages directly (no buildInitialMessages)
     * - No reminder injection (messages already have them)
     * - Supports model/LLM setting overrides
     *
     * @param options - Replay options including messages, system prompt, and callbacks
     * @returns Result with content, messages, completion status, and optional error
     */
    async replayFromLog(options) {
        const { conversationId = 'replay', documentId = 'replay', threadId, systemPrompt, messages, modelOverride, toolChoice = 'auto', maxIterations, maxTokens, temperature, extraBody, extraHeaders, thinking, stream = true, callbacks, } = options;
        // Store execution context for use in tool loop
        this.currentConversationId = conversationId;
        this.currentdocumentId = documentId;
        this.currentThreadId = threadId || `${this.name}_replay_${Date.now()}`;
        // Set streaming mode
        const originalStream = this.stream;
        this.stream = stream;
        // Temporarily override max response tokens if specified
        const originalMaxTokens = this.maxResponseTokens;
        if (maxTokens !== undefined) {
            this.maxResponseTokens = maxTokens;
        }
        // Push agent context for tracing
        pushAgentContext({
            conversationId: this.currentConversationId,
            agentName: this.name,
            threadId: this.currentThreadId,
            documentId: this.currentdocumentId,
        });
        try {
            // Build tool loop options with callbacks and model override
            const toolLoopOptions = {
                systemPrompt,
                maxIterations: maxIterations ?? this.maxToolLoopIterations,
                modelOverride,
                toolChoice,
                temperature,
                extraBody,
                extraHeaders,
                thinking,
                onChunk: callbacks?.onChunk,
                onToolCall: callbacks?.onToolCall,
                onToolResult: callbacks?.onToolResult,
                onToolStart: callbacks?.onToolStart,
                onOperationField: callbacks?.onOperationField,
                onAgentChunk: callbacks?.onAgentChunk,
                onAgentToolStart: callbacks?.onAgentToolStart,
                onAgentOperationField: callbacks?.onAgentOperationField,
                onAgentToolCall: callbacks?.onAgentToolCall,
                onAgentToolResult: callbacks?.onAgentToolResult,
            };
            // Run the tool loop with provided messages
            const loopResult = await this.runToolLoop([...messages], toolLoopOptions);
            return {
                content: loopResult.content,
                messages: loopResult.messages,
                tokensUsed: loopResult.tokensUsed,
                completed: loopResult.completed,
                error: loopResult.error,
            };
        }
        catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error(`[${this.name}] replayFromLog error:`, error);
            return {
                content: '',
                messages,
                completed: false,
                error: errorMsg,
            };
        }
        finally {
            // Restore original settings
            this.stream = originalStream;
            this.maxResponseTokens = originalMaxTokens;
            // Pop agent context
            popAgentContext();
        }
    }
}
export default AgentBaseClass;
